;(() => {
  const configEl = document.getElementById('Lobster2026GwpConfig')
  if (!configEl) return

  let config
  try {
    config = JSON.parse(configEl.textContent)
  } catch {
    return
  }

  if (!config?.enabled || !config.variantId || !config.code) return

  const CODE = String(config.code).trim().toUpperCase()
  const VARIANT_ID = Number(config.variantId)
  const THRESHOLD = Number(config.threshold) || 20000
  const PROPERTY_KEY = config.propertyKey || '_gwp'
  const PROPERTY_VALUE = config.propertyValue || 'lobster2026'
  const GIFT_TITLE = config.giftTitle || 'Maine Lobster Tails'
  const CART_ROOT = window.Shopify?.routes?.root || '/'

  let syncing = false
  let queued = false
  let pendingCheckoutForm = null
  let ineligible = false
  let lastState = {
    active: false,
    qualified: false,
    gift: null,
    variantItem: null,
    cartTotal: 0
  }

  function shouldBlockCheckout() {
    return Boolean(lastState.active && !lastState.qualified)
  }

  function shouldHoldCheckout() {
    if (syncing) return true
    if (shouldBlockCheckout()) return true
    return Boolean(lastState.active && lastState.qualified && !lastState.gift && !lastState.variantItem)
  }

  function formatMoney(cents) {
    const safeCents = Math.max(0, Number(cents) || 0)
    if (window.Shopify?.formatMoney && config.moneyFormat) {
      const formatted = window.Shopify.formatMoney(safeCents, config.moneyFormat)
      if (safeCents % 100 === 0) return formatted.replace(/[.,]00$/, '')
      return formatted
    }
    const dollars = safeCents / 100
    return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`
  }

  function urlHasCode() {
    const params = new URLSearchParams(window.location.search)
    return (params.get('discount') || '').trim().toUpperCase() === CODE
  }

  function normalizeCode(discount) {
    return String(discount?.code || '')
      .trim()
      .toUpperCase()
  }

  function otherDiscountApplied(cart) {
    return (cart?.discount_codes || []).some((discount) => {
      return normalizeCode(discount) !== CODE && Boolean(discount.applicable)
    })
  }

  function codeEntry(cart) {
    return (cart?.discount_codes || []).find((discount) => normalizeCode(discount) === CODE)
  }

  function hasCode(cart) {
    if (otherDiscountApplied(cart)) return false
    return Boolean(codeEntry(cart))
  }

  function codeIsApplicable(cart) {
    return Boolean(codeEntry(cart)?.applicable)
  }

  function isGiftItem(item) {
    const properties = item?.properties || {}
    return properties[PROPERTY_KEY] === PROPERTY_VALUE
  }

  function findGiftItem(cart) {
    return (cart?.items || []).find(isGiftItem)
  }

  function findVariantItem(cart) {
    return (cart?.items || []).find((item) => Number(item.variant_id) === VARIANT_ID)
  }

  function qualifyingTotal(cart) {
    return (cart?.items || []).reduce((sum, item) => {
      if (isGiftItem(item)) return sum
      return sum + (Number(item.original_line_price) || 0)
    }, 0)
  }

  function buildState(cart) {
    const present = hasCode(cart)
    const applicable = present && codeIsApplicable(cart)
    const active = present || (!otherDiscountApplied(cart) && urlHasCode())
    const qualifying = qualifyingTotal(cart)
    const remaining = Math.max(0, THRESHOLD - qualifying)
    const qualified = qualifying >= THRESHOLD
    const gift = findGiftItem(cart)
    const variantItem = findVariantItem(cart)
    const itemCount = Number(cart?.item_count) || 0
    const cartTotal = Number(cart?.total_price) || 0
    let message = ''

    if (!active) {
      return {
        active: false,
        qualified: false,
        qualifying,
        remaining,
        present,
        applicable,
        gift,
        variantItem,
        message,
        itemCount,
        cartTotal
      }
    }

    if (!qualified) {
      message =
        qualifying > 0
          ? `${CODE} requires a ${formatMoney(THRESHOLD)} minimum. You're ${formatMoney(remaining)} away.`
          : `${CODE} requires a ${formatMoney(THRESHOLD)} minimum before checkout.`
    } else if (!applicable) {
      message = ''
    } else if (gift || variantItem) {
      message = `You've reached ${formatMoney(THRESHOLD)} — ${GIFT_TITLE} has been added to your cart.`
    } else {
      message = `You've reached ${formatMoney(THRESHOLD)} — ${GIFT_TITLE} will be added automatically.`
    }

    return {
      active,
      present,
      applicable,
      qualified,
      qualifying,
      remaining,
      gift,
      variantItem,
      message,
      itemCount,
      cartTotal
    }
  }

  function updateBars(state) {
    lastState = state
    document.querySelectorAll('[data-lobster-gwp-bar]').forEach((bar) => {
      if (!state.active || !state.itemCount || (state.qualified && !state.applicable)) {
        bar.hidden = true
        return
      }

      bar.hidden = false
      bar.classList.toggle('cart-gwp--qualified', state.qualified)

      const message = bar.querySelector('[data-lobster-gwp-message]')
      if (message) message.textContent = state.message
    })
  }

  function storeMinimumBlocks(form, cartTotal) {
    if (form.dataset.enableMinimumOrder !== 'true') return false
    const threshold = parseFloat(form.dataset.minimumOrderSubtotal) * 100
    return cartTotal > 0 && cartTotal < threshold
  }

  function refreshCheckoutButtons(state) {
    const lobsterLocked = Boolean(state.active && !state.qualified)

    document.querySelectorAll('form[action*="cart"]').forEach((form) => {
      const checkoutBtn = form.querySelector('.cart__checkout')
      const additionalCheckout = form.querySelector('.additional-checkout-buttons')
      const belowMinimum = lobsterLocked || storeMinimumBlocks(form, state.cartTotal)

      if (checkoutBtn) {
        checkoutBtn.disabled = belowMinimum
        checkoutBtn.setAttribute('aria-disabled', belowMinimum ? 'true' : 'false')
        if (belowMinimum) checkoutBtn.classList.remove('btn--loading')
      }

      if (additionalCheckout) {
        additionalCheckout.hidden = belowMinimum
      }
    })
  }

  async function getCart() {
    const response = await fetch(`${CART_ROOT}cart.js?t=${Date.now()}`, { credentials: 'same-origin' })
    if (!response.ok) throw new Error('Unable to load cart')
    return response.json()
  }

  function hideAddedToast() {
    const toast = document.getElementById('CartAddedToast')
    if (toast) {
      toast.classList.remove('is-visible')
      toast.hidden = true
    }
    document.documentElement.classList.remove('cart-added-toast-open')
    if (typeof window.SLIDECART_CLOSE === 'function') window.SLIDECART_CLOSE()
  }

  async function addGift(options = {}) {
    if (options.suppressToast) window.__dsSuppressCartToast = true

    try {
      const response = await fetch(`${CART_ROOT}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: VARIANT_ID,
              quantity: 1,
              properties: {
                [PROPERTY_KEY]: PROPERTY_VALUE
              }
            }
          ]
        })
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        console.warn('Unable to add lobster tails promo item', error)
        return null
      }

      const added = await response.json().catch(() => null)
      const cart = await getCart()
      if (cart && options.suppressToast) cart._addedNotice = added
      return cart
    } finally {
      if (options.suppressToast) window.__dsSuppressCartToast = false
    }
  }

  function showAddedToast(cart) {
    const added = cart?._addedNotice
    if (!added) return
    delete cart._addedNotice
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: added } }))
  }

  function showInvalidMessage() {
    const fallback = 'This discount code is invalid.'
    document.querySelectorAll('cart-discount-code').forEach((el) => {
      const message = el.querySelector('[data-discount-message]')
      if (!message) return
      let text = fallback
      try {
        const locales = JSON.parse(el.querySelector('[data-discount-locales]')?.textContent || '{}')
        text = locales.invalidMessage || fallback
      } catch {
        text = fallback
      }
      message.hidden = false
      message.textContent = text
      message.classList.add('cart-discount-code__message--error')
    })
  }

  async function removeDiscountCode(cart) {
    const remaining = (cart?.discount_codes || [])
      .map((discount) => String(discount?.code || '').trim())
      .filter((code) => code && code.toUpperCase() !== CODE)

    const response = await fetch(`${CART_ROOT}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        discount: remaining.join(',')
      })
    })

    if (!response.ok) return null
    return response.json()
  }

  async function clearRejectedCode(cart) {
    hideAddedToast()
    if (cart?._addedNotice) delete cart._addedNotice
    let next = cart
    const gift = findGiftItem(next)
    if (gift) {
      const removedGift = await removeGift(gift.key)
      if (removedGift) next = removedGift
    }

    if (codeEntry(next)) {
      const removedCode = await removeDiscountCode(next)
      if (removedCode) next = removedCode
    }

    const state = buildState(next)
    updateBars(state)
    refreshCheckoutButtons(state)
    notifyCartChanged(next)
    showInvalidMessage()
    return next
  }

  async function removeGift(key) {
    const response = await fetch(`${CART_ROOT}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: key,
        quantity: 0
      })
    })

    if (!response.ok) return null
    return getCart()
  }

  function notifyCartChanged(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }))
    document.dispatchEvent(new CustomEvent('cart:build'))
  }

  function continueCheckout() {
    const form = pendingCheckoutForm
    pendingCheckoutForm = null
    if (!form) return

    if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'checkout'
      input.value = '1'
      form.appendChild(input)
    }

    HTMLFormElement.prototype.submit.call(form)
  }

  function clearPendingCheckout() {
    const form = pendingCheckoutForm
    pendingCheckoutForm = null
    form?.querySelector('.cart__checkout')?.classList.remove('btn--loading')
  }

  async function sync(options = {}) {
    if (syncing) {
      queued = true
      return
    }
    syncing = true

    try {
      let cart = options.cart || (await getCart())
      let state = buildState(cart)
      updateBars(state)
      refreshCheckoutButtons(state)

      if (!state.active) {
        if (state.gift) {
          const updated = await removeGift(state.gift.key)
          if (updated) {
            cart = updated
            state = buildState(cart)
            updateBars(state)
            refreshCheckoutButtons(state)
            notifyCartChanged(cart)
          }
        }
        if (pendingCheckoutForm) continueCheckout()
        return
      }

      let changed = false

      if (state.qualified && state.present && !state.applicable) {
        if (!state.gift && !state.variantItem && !ineligible) {
          ineligible = true
          const updated = await addGift({ suppressToast: true })
          if (updated && codeIsApplicable(updated)) {
            ineligible = false
            showAddedToast(updated)
            cart = updated
            state = buildState(cart)
            updateBars(state)
            refreshCheckoutButtons(state)
            changed = true
          } else {
            cart = await clearRejectedCode(updated || cart)
            state = buildState(cart)
            changed = true
          }
        } else {
          ineligible = true
          cart = await clearRejectedCode(cart)
          state = buildState(cart)
          changed = true
        }
      } else if (state.qualified && state.applicable) {
        ineligible = false
        if (!state.gift && !state.variantItem) {
          const updated = await addGift()
          if (updated) {
            cart = updated
            state = buildState(cart)
            updateBars(state)
            refreshCheckoutButtons(state)
            changed = true
          }
        }
      } else if (state.gift) {
        const updated = await removeGift(state.gift.key)
        if (updated) {
          cart = updated
          state = buildState(cart)
          updateBars(state)
          refreshCheckoutButtons(state)
          changed = true
        }
      }

      if (changed) notifyCartChanged(cart)

      if (pendingCheckoutForm) {
        if (state.active && !state.qualified) {
          clearPendingCheckout()
          return
        }
        continueCheckout()
      }
    } catch (error) {
      console.warn('LOBSTER2026 gift sync failed', error)
      if (pendingCheckoutForm) {
        if (shouldBlockCheckout()) {
          clearPendingCheckout()
        } else {
          continueCheckout()
        }
      }
    } finally {
      syncing = false
      if (queued) {
        queued = false
        sync()
      }
    }
  }

  window.Lobster2026Gwp = {
    matches(code) {
      return String(code || '')
        .trim()
        .toUpperCase() === CODE
    },
    isCheckoutBlocked() {
      return shouldBlockCheckout()
    },
    async ensureInCart() {
      const cart = await getCart()
      const state = buildState(cart)
      if (!state.qualified || !state.applicable) return cart
      if (findGiftItem(cart) || findVariantItem(cart)) return cart
      return addGift()
    },
    async removeFromCart() {
      const cart = await getCart()
      const gift = findGiftItem(cart)
      if (!gift) return cart
      return removeGift(gift.key)
    }
  }

  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail?.cart
    if (cart?.items && cart.discount_codes) {
      sync({ cart })
      return
    }
    sync()
  })

  document.addEventListener('ajaxProduct:added', () => {
    sync()
  })

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target
      if (!(form instanceof HTMLFormElement)) return

      const action = form.getAttribute('action') || ''
      if (!action.includes('/cart')) return
      if (!form.querySelector('[name="checkout"]')) return

      const submitter = event.submitter
      if (submitter && submitter.name && submitter.name !== 'checkout') return
      if (!shouldHoldCheckout()) return

      event.preventDefault()
      event.stopImmediatePropagation()
      form.querySelector('.cart__checkout')?.classList.remove('btn--loading')

      if (shouldBlockCheckout()) return

      pendingCheckoutForm = form
      sync()
    },
    true
  )

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync(), { once: true })
  } else {
    sync()
  }
})()
